import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Title,
  Text,
  Textarea,
  Select,
  Button,
  Stack,
  Group,
  TextInput,
  NumberInput,
  ActionIcon,
  Badge,
  Alert,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { IconArrowLeft, IconPlus, IconTrash } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useCreateRFQ, useUpdateRFQ, useRFQ } from "../hooks/useRFQ";
import { notifications } from "@mantine/notifications";
import { vendorCategoriesApi } from "../../../api/vendorCategories";
import { adminVendorsApi } from "../../../api/adminVendors";
import { useQuery } from "@tanstack/react-query";
import { MultiSelect } from "@mantine/core";
import dayjs from "dayjs";

interface RFQItem {
  id: string;
  itemName: string;
  description?: string;
  quantity: number;
  unit: string;
  order: number;
}

const DEFAULT_UNITS = [
  { value: "units", label: "Units" },
  { value: "pieces", label: "Pieces" },
  { value: "kg", label: "Kilograms" },
  { value: "liters", label: "Liters" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "meters", label: "Meters" },
  { value: "sets", label: "Sets" },
];

export function RFQForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const createRFQ = useCreateRFQ();
  const updateRFQ = useUpdateRFQ();
  const { data: existingRFQ, isLoading: isLoadingRFQ } = useRFQ(id);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "" as "GOODS" | "SERVICES" | "",
    deadline: new Date(),
    currency: "MVR" as "MVR" | "USD",
  });

  const [items, setItems] = useState<RFQItem[]>([]);
  const [selectedVendorCategories, setSelectedVendorCategories] = useState<string[]>([]);
  const [selectedVendorSubcategories, setSelectedVendorSubcategories] = useState<string[]>([]);
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);

  const { data: vendorCategories } = useQuery({
    queryKey: ["vendor-categories"],
    queryFn: () => vendorCategoriesApi.listCategories(),
  });

  const { data: vendors } = useQuery({
    queryKey: ["vendors"],
    queryFn: () => adminVendorsApi.listVendors({ status: "ACTIVE" }),
  });

  // Flatten subcategories from all categories
  const allSubcategories = vendorCategories?.flatMap((cat) =>
    cat.subcategories.map((sub) => ({
      ...sub,
      categoryName: cat.name,
    }))
  ) || [];

  // Load existing RFQ data when in edit mode
  useEffect(() => {
    if (isEditMode && existingRFQ) {
      setFormData({
        title: existingRFQ.title,
        description: existingRFQ.description,
        category: existingRFQ.category,
        deadline: new Date(existingRFQ.deadline),
        currency: existingRFQ.currency,
      });
      setItems(
        existingRFQ.items.map((item) => ({
          id: item.id,
          itemName: item.itemName,
          description: item.description || "",
          quantity: item.quantity,
          unit: item.unit,
          order: item.order,
        }))
      );
      setSelectedVendorCategories(
        existingRFQ.vendorCategories?.map((vc) => vc.categoryId) || []
      );
      setSelectedVendorSubcategories(
        existingRFQ.vendorSubcategories?.map((vsc) => vsc.subcategoryId) || []
      );
      setSelectedVendors(
        existingRFQ.selectedVendorIds || []
      );
    }
  }, [isEditMode, existingRFQ]);

  const addItem = () => {
    const newItem: RFQItem = {
      id: Date.now().toString(),
      itemName: "",
      description: "",
      quantity: 1,
      unit: "units",
      order: items.length + 1,
    };
    setItems([...items, newItem]);
  };

  const removeItem = (id: string) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const updateItem = (id: string, field: keyof RFQItem, value: any) => {
    setItems(
      items.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.description || !formData.category) {
      notifications.show({
        title: "Validation Error",
        message: "Please fill in all required fields",
        color: "red",
      });
      return;
    }

    if (items.length === 0) {
      notifications.show({
        title: "Validation Error",
        message: "Please add at least one item",
        color: "red",
      });
      return;
    }

    const invalidItems = items.filter((item) => !item.itemName.trim());
    if (invalidItems.length > 0) {
      notifications.show({
        title: "Validation Error",
        message: "Please provide item names for all items",
        color: "red",
      });
      return;
    }

    const submitData = {
      title: formData.title,
      description: formData.description,
      category: formData.category as "GOODS" | "SERVICES",
      deadline: formData.deadline.toISOString(),
      currency: formData.currency,
      items: items.map((item) => ({
        itemName: item.itemName,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        order: item.order,
      })),
      vendorCategoryIds: selectedVendorCategories.length > 0 ? selectedVendorCategories : undefined,
      vendorSubcategoryIds: selectedVendorSubcategories.length > 0 ? selectedVendorSubcategories : undefined,
      selectedVendorIds: selectedVendors.length > 0 ? selectedVendors : undefined,
    };

    if (isEditMode && id) {
      updateRFQ.mutate(
        {
          id,
          data: submitData,
        },
        {
          onSuccess: () => {
            navigate(`/rfq/${id}`);
          },
        }
      );
    } else {
      createRFQ.mutate(submitData, {
        onSuccess: (data) => {
          navigate(`/rfq/${data.id}`);
        },
      });
    }
  };

  if (isEditMode && isLoadingRFQ) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading RFQ...</Text>
      </Container>
    );
  }

  if (isEditMode && existingRFQ && existingRFQ.status !== "DRAFT") {
    return (
      <Container size="xl" py="xl">
        <Alert color="red" title="Cannot Edit">
          Only draft RFQs can be edited. This RFQ is in {existingRFQ.status} status.
        </Alert>
        <Button mt="md" onClick={() => navigate(`/rfq/${id}`)}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group mb="xl">
        <ActionIcon variant="subtle" onClick={() => navigate(isEditMode && id ? `/rfq/${id}` : "/rfq")}>
          <IconArrowLeft size={20} />
        </ActionIcon>
        <Title order={2}>{isEditMode ? "Edit RFQ" : "Create RFQ"}</Title>
      </Group>

      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Paper shadow="sm" p="md">
            <Stack gap="md">
              <TextInput
                label="Title"
                placeholder="Enter RFQ title"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />

              <Textarea
                label="Description"
                placeholder="Enter RFQ description"
                required
                minRows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />

              <Group grow>
                <Select
                  label="Category"
                  placeholder="Select category"
                  required
                  data={[
                    { value: "GOODS", label: "Goods" },
                    { value: "SERVICES", label: "Services" },
                  ]}
                  value={formData.category}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      category: (value as "GOODS" | "SERVICES") || "",
                    })
                  }
                />

                <DateTimePicker
                  label="Deadline"
                  placeholder="Select deadline"
                  required
                  value={formData.deadline}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      deadline: value || new Date(),
                    })
                  }
                />

                <Select
                  label="Currency"
                  data={[
                    { value: "MVR", label: "MVR" },
                    { value: "USD", label: "USD" },
                  ]}
                  value={formData.currency}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      currency: (value as "MVR" | "USD") || "MVR",
                    })
                  }
                />
              </Group>
            </Stack>
          </Paper>

          <Paper shadow="sm" p="md">
            <Stack gap="md">
              <Title order={4}>Vendor Selection</Title>
              <Text size="sm" c="dimmed">
                Select vendors, subcategories, or categories. The RFQ will be sent to all matching vendors when you send it.
              </Text>

              <MultiSelect
                label="Select Specific Vendors (Optional)"
                placeholder="Choose specific vendors"
                data={
                  vendors?.map((vendor) => ({
                    value: vendor.id,
                    label: vendor.businessName || vendor.email,
                  })) || []
                }
                value={selectedVendors}
                onChange={setSelectedVendors}
                searchable
                clearable
              />

              <MultiSelect
                label="Vendor Subcategories (Optional)"
                placeholder="Select vendor subcategories"
                data={
                  allSubcategories.map((sub) => ({
                    value: sub.id,
                    label: `${sub.categoryName} - ${sub.name}`,
                  })) || []
                }
                value={selectedVendorSubcategories}
                onChange={setSelectedVendorSubcategories}
                searchable
                clearable
              />

              <MultiSelect
                label="Vendor Categories (Optional)"
                placeholder="Select vendor categories"
                data={
                  vendorCategories?.map((cat) => ({
                    value: cat.id,
                    label: cat.name,
                  })) || []
                }
                value={selectedVendorCategories}
                onChange={setSelectedVendorCategories}
                searchable
                clearable
              />
            </Stack>
          </Paper>

          <Paper shadow="sm" p="md">
            <Group justify="space-between" mb="md">
              <Title order={4}>Items</Title>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={addItem}
                variant="light"
              >
                Add Item
              </Button>
            </Group>

            {items.length === 0 ? (
              <Stack align="center" py="xl">
                <Text c="dimmed">No items added yet. Click "Add Item" to get started.</Text>
              </Stack>
            ) : (
              <Stack gap="md">
                {items.map((item, index) => (
                  <Paper key={item.id} p="md" withBorder>
                    <Group justify="space-between" mb="sm">
                      <Badge>Item {index + 1}</Badge>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => removeItem(item.id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                    <Stack gap="sm">
                      <TextInput
                        label="Item Name"
                        placeholder="Enter item name"
                        required
                        value={item.itemName}
                        onChange={(e) =>
                          updateItem(item.id, "itemName", e.target.value)
                        }
                      />
                      <Textarea
                        label="Description"
                        placeholder="Enter item description (optional)"
                        value={item.description}
                        onChange={(e) =>
                          updateItem(item.id, "description", e.target.value)
                        }
                      />
                      <Group grow>
                        <NumberInput
                          label="Quantity"
                          placeholder="Enter quantity"
                          required
                          min={1}
                          value={item.quantity}
                          onChange={(value) =>
                            updateItem(item.id, "quantity", Number(value) || 1)
                          }
                        />
                        <Select
                          label="Unit"
                          data={DEFAULT_UNITS}
                          value={item.unit}
                          onChange={(value) =>
                            updateItem(item.id, "unit", value || "units")
                          }
                        />
                      </Group>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>

          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => navigate("/rfq")}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isEditMode ? updateRFQ.isPending : createRFQ.isPending}
              disabled={items.length === 0}
            >
              {isEditMode ? "Update RFQ" : "Create RFQ"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Container>
  );
}

